import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { useGetTutorial } from "../../hooks/Tutorial/useGetTutorial"
import { tutorialMemory } from "../../Objects/Memory/Memory"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { AlertCircle, FolderOpen, PlayCircle, Youtube } from "lucide-react"
import { ETutorialAudienceType } from "../../Enum/ETutorialAudienceType"
import { extractYoutubeId } from "../../Objects/Generics/Helps"
import { BookOpen, Video, Sparkles, Clock3 } from "lucide-react"

export const TutorialsPage: React.FC = () => {
  const { handleGetTutorial } = useGetTutorial()

  const { t } = useTranslation("ownerOnboarding")

  const { userEmployee } = useEmployerLogged()

  useEffect(() => {
    handleGetTutorial(() => {}, undefined, ETutorialAudienceType.Store)
  }, [])

  const groupedTutorials = useMemo(() => {
    const grouped = new Map<string, typeof tutorialMemory.listData>()

    tutorialMemory.listData.forEach((item) => {
      const key = item.category.trim() || "Sem categoria"

      const current = grouped.get(key) ?? []

      current.push(item)

      grouped.set(key, current)
    })

    return Array.from(grouped.entries())
  }, [tutorialMemory.listData.length])

  if (groupedTutorials.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="w-full h-[calc(100%-25px)] overflow-y-auto p-3 sm:p-4 md:p-5">
          <section className="relative overflow-hidden rounded-lg border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-5 sm:p-6 md:p-8 shadow-sm">
            <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-orange-100/60 blur-2xl" />

            <div className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-amber-100/50 blur-2xl" />

            <div className="relative flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-orange-100 shadow-inner">
                <Video className="h-8 w-8 sm:h-10 sm:w-10 text-orange-600" />
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-[11px] sm:text-xs font-black uppercase tracking-wider text-orange-600 shadow-sm backdrop-blur">
                <BookOpen className="h-3.5 w-3.5" />
                Tutoriais
              </div>

              <h2 className="mt-4 text-lg sm:text-xl md:text-2xl font-extrabold text-slate-900 leading-tight">
                Nenhum tutorial disponível no momento
              </h2>

              <p className="mt-3 max-w-xl text-sm sm:text-base leading-relaxed text-slate-600">
                Assim que novos vídeos forem cadastrados pelo administrador, eles aparecerão aqui para você assistir com
                facilidade.
              </p>

              {userEmployee?.isMaster && (
                <button
                  onClick={() => window.dispatchEvent(new Event("open-owner-onboarding"))}
                  className="mt-5 rounded-2xl border-4 border-orange-500 bg-white px-5 py-3 text-sm font-black text-orange-700 shadow-lg hover:bg-orange-50"
                  data-interaction-description-key="interactionDescriptions:pages.tutorials.tutorialsPage.button.windowDispatchEventNewEventOpenOwnerOnboarding"
                >
                  {t("open_manual")}
                </button>
              )}

              <div className="mt-6 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 text-left shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-slate-800">
                    <Video className="h-4 w-4 text-orange-500" />

                    <span className="text-sm font-semibold">Vídeos</span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-500">Conteúdos em vídeo serão exibidos aqui.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 text-left shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-slate-800">
                    <Sparkles className="h-4 w-4 text-amber-500" />

                    <span className="text-sm font-semibold">Novidades</span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-500">Novos tutoriais aparecerão automaticamente.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 text-left shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-slate-800">
                    <Clock3 className="h-4 w-4 text-orange-500" />

                    <span className="text-sm font-semibold">Em breve</span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-500">Volte mais tarde para conferir atualizações.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <div className="w-full p-2 md:p-2 space-y-5 md:space-y-6 h-[calc(100%-25px)] overflow-y-auto">
        {userEmployee?.isMaster && (
          <section className="rounded-2xl border-4 border-orange-500 bg-white p-4 text-slate-900 shadow-lg ring-4 ring-orange-100">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-orange-600">{t("manual_badge")}</p>
                <h2 className="mt-1 text-lg font-black">{t("manual_title")}</h2>
                <p className="mt-1 text-sm text-slate-600">{t("manual_description")}</p>
              </div>

              <button
                onClick={() => window.dispatchEvent(new Event("open-owner-onboarding"))}
                className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-black text-white shadow-md ring-2 ring-orange-200 hover:bg-orange-600"
                data-interaction-description-key="interactionDescriptions:pages.tutorials.tutorialsPage.button.windowDispatchEventNewEventOpenOwnerOnboarding2"
              >
                {t("open_manual")}
              </button>
            </div>
          </section>
        )}

        {groupedTutorials.map(([category, items], index) => (
          <Animate key={category} type={AnimationTypes.slideRightIn} duration={`${0.2 + index * 0.15}s`}>
            <section className="w-full overflow-hidden rounded-lg border border-orange-100 bg-white shadow-sm transition-all hover:shadow-md">
              <div className="flex items-center justify-between gap-3 border-b border-orange-50 bg-gradient-to-r from-orange-50 via-white to-white px-4 py-3 md:px-2 md:py-2">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                    <FolderOpen size={18} />
                  </div>

                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-black uppercase tracking-wide text-orange-600 md:text-base">
                      {category}
                    </h2>
                    <p className="text-xs text-slate-500 md:text-sm">
                      {items.length} {items.length === 1 ? "tutorial" : "tutoriais"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-2 py-2 md:px-3 md:py-2">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {items.map((item) => {
                    const videoId = extractYoutubeId(item.youtubeUrl)

                    return (
                      <article
                        key={item.id}
                        className="group w-full min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                      >
                        <div className="p-3 md:p-4">
                          <div className="mb-3 flex items-start gap-3">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
                              <Youtube size={20} />
                            </div>

                            <div className="min-w-0 flex-1">
                              <h3 className="line-clamp-2 text-sm font-bold text-slate-900 md:text-base">
                                {item.title}
                              </h3>

                              <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                                <PlayCircle size={14} />
                                <span>Vídeo tutorial</span>
                              </div>
                            </div>
                          </div>

                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner">
                            <div className="aspect-video">
                              {videoId ? (
                                <iframe
                                  className="h-full w-full"
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={item.title}
                                  loading="lazy"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                  allowFullScreen
                                />
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-slate-500">
                                  <AlertCircle size={18} className="text-orange-500" />
                                  <span className="text-xs md:text-sm font-medium">Link do YouTube inválido</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    )
                  })}
                </div>
              </div>
            </section>
          </Animate>
        ))}
      </div>
    </Animate>
  )
}
